import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { tg } from '../services/telegram';

export interface WaveformComment {
  id: string;
  timeRatio: number; // 0 to 1
  avatarUrl?: string;
  emoji?: string;
  author?: string;
}

interface SoundcloudWaveformProps {
  currentTime: number;
  duration: number;
  onSeek: (seconds: number) => void;
  trackId?: string | number;
  trackTitle?: string;
  height?: number;
  barWidth?: number;
  barGap?: number;
  playedColor?: string;
  unplayedColor?: string;
  needleColor?: string;
  showBadge?: boolean;
  comments?: WaveformComment[];
  className?: string;
}

// Deterministic Pseudo-Random Generator (Mulberry32) for stable waveform per track
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function stringToSeed(str: string): number {
  let hash = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    hash = Math.imul(hash ^ str.charCodeAt(i), 3432918353);
    hash = (hash << 13) | (hash >>> 19);
  }
  return hash >>> 0;
}

/**
 * Generate a realistic musical waveform envelope with intro, verses, drops, and outro
 */
function generateWaveformData(seedStr: string, count: number): number[] {
  const seed = stringToSeed(seedStr || 'soundcloud_master_track');
  const prng = mulberry32(seed);
  const data: number[] = new Array(count);

  for (let i = 0; i < count; i++) {
    const progress = i / count;

    // Macro envelope: dynamic curve of a typical electronic / pop / rock track
    let envelope = 0.45;
    if (progress < 0.08) {
      // Intro: gentle fade in
      envelope = 0.2 + (progress / 0.08) * 0.35;
    } else if (progress < 0.26) {
      // Verse 1: steady groove
      envelope = 0.45 + Math.sin(progress * Math.PI * 4) * 0.12;
    } else if (progress < 0.34) {
      // Build-up 1
      const p = (progress - 0.26) / 0.08;
      envelope = 0.5 + p * 0.35;
    } else if (progress < 0.52) {
      // Drop 1 / Chorus: maximum power
      envelope = 0.82 + Math.sin(progress * 28) * 0.12;
    } else if (progress < 0.64) {
      // Breakdown / Verse 2: atmospheric dip
      envelope = 0.38 + Math.sin(progress * Math.PI * 6) * 0.14;
    } else if (progress < 0.72) {
      // Build-up 2
      const p = (progress - 0.64) / 0.08;
      envelope = 0.52 + p * 0.4;
    } else if (progress < 0.90) {
      // Drop 2 / Climax: loud and dense
      envelope = 0.88 + Math.sin(progress * 32) * 0.1;
    } else {
      // Outro: decay to zero
      const p = (progress - 0.90) / 0.10;
      envelope = (1 - p) * 0.65;
    }

    // Micro rhythm: kick / snare pulse every few bars
    const beatPulse = (i % 4 === 0 ? 0.18 : i % 2 === 0 ? 0.08 : 0);

    // High frequency organic jitter
    const noise = (prng() - 0.5) * 0.28;

    // Final amplitude clamped to [0.12, 1.0]
    const amp = Math.max(0.12, Math.min(1.0, envelope + beatPulse + noise));
    data[i] = amp;
  }

  return data;
}

export const SoundcloudWaveform: React.FC<SoundcloudWaveformProps> = ({
  currentTime,
  duration,
  onSeek,
  trackId = 'default',
  trackTitle = '',
  height = 68,
  barWidth = 2.5,
  barGap = 1.8,
  playedColor,
  unplayedColor = 'rgba(255, 255, 255, 0.44)',
  needleColor = '#ffffff',
  showBadge = true,
  comments = [],
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [dragRatio, setDragRatio] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(360);

  // Last haptic triggered second to simulate mechanical scrubber clicks
  const lastHapticSecondRef = useRef<number>(-1);

  // Active progress ratio (either live time or drag preview)
  const currentRatio = useMemo(() => {
    if (isDragging && dragRatio !== null) {
      return dragRatio;
    }
    return duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;
  }, [isDragging, dragRatio, currentTime, duration]);

  // Display time in badge (preview when dragging, current when playing)
  const displayCurrentTime = useMemo(() => {
    if (isDragging && dragRatio !== null) {
      return dragRatio * duration;
    }
    return currentTime;
  }, [isDragging, dragRatio, currentTime, duration]);

  // Format time (mm:ss or hh:mm:ss if > 1 hour)
  const formatTime = (sec: number) => {
    if (!sec || isNaN(sec) || sec < 0) return '00:00';
    const totalSec = Math.floor(sec);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Measure container width accurately on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        if (w > 0) setContainerWidth(w);
      }
    };
    updateWidth();

    const observer = new ResizeObserver(() => updateWidth());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Compute number of bars that fit horizontally
  const barCount = useMemo(() => {
    const slot = barWidth + barGap;
    return Math.max(20, Math.floor(containerWidth / slot));
  }, [containerWidth, barWidth, barGap]);

  // Waveform amplitude dataset for the current track
  const waveformAmplitudes = useMemo(() => {
    return generateWaveformData(`${trackId}_${trackTitle}`, barCount);
  }, [trackId, trackTitle, barCount]);

  // Canvas drawing loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = containerWidth;
    const h = height;

    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, w, h);

    // Baseline calculation: top amplitude occupies ~70% of height, bottom reflection occupies ~30%
    const baselineGap = 2; // px gap separating upper and lower waveform halves
    const topMaxHeight = (h - baselineGap) * 0.70;
    const bottomMaxHeight = (h - baselineGap) * 0.30;
    const baselineY = topMaxHeight;

    const slot = barWidth + barGap;
    const scrubberX = currentRatio * w;

    // Resolve played color dynamically from CSS theme variable or default cyan/amber
    let activePlayedColor = playedColor;
    if (!activePlayedColor) {
      // Default to CSS theme accent (--amber or --accent or cyan)
      const computed = getComputedStyle(canvas).getPropertyValue('--amber').trim();
      activePlayedColor = computed || '#00d2be';
    }

    // Draw bars
    for (let i = 0; i < barCount; i++) {
      const x = i * slot;
      const barCenterX = x + barWidth / 2;
      const isPlayed = barCenterX <= scrubberX;
      const amp = waveformAmplitudes[i] ?? 0.5;

      const topH = Math.max(3, amp * topMaxHeight);
      const bottomH = Math.max(2, amp * bottomMaxHeight * 0.85);

      ctx.fillStyle = isPlayed ? activePlayedColor : unplayedColor;

      // Upper bar (from baseline upwards, rounded top)
      const topY = baselineY - topH;
      ctx.beginPath();
      const radius = Math.min(barWidth / 2, 1.5);
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, topY, barWidth, topH, [radius, radius, 0, 0]);
      } else {
        ctx.rect(x, topY, barWidth, topH);
      }
      ctx.fill();

      // Lower bar (from baseline + gap downwards, rounded bottom)
      const bottomY = baselineY + baselineGap;
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, bottomY, barWidth, bottomH, [0, 0, radius, radius]);
      } else {
        ctx.rect(x, bottomY, barWidth, bottomH);
      }
      ctx.fill();
    }

    // Scrubber Needle line (vertical thin hair separating played and unplayed)
    ctx.fillStyle = needleColor;
    ctx.fillRect(Math.max(0, Math.min(w - 1.5, scrubberX - 0.75)), 0, 1.5, h);
  }, [
    containerWidth,
    height,
    barCount,
    waveformAmplitudes,
    currentRatio,
    barWidth,
    barGap,
    playedColor,
    unplayedColor,
    needleColor,
  ]);

  // Calculate ratio from pointer event coordinates
  const calculateRatio = useCallback(
    (clientX: number): number => {
      if (!containerRef.current) return 0;
      const rect = containerRef.current.getBoundingClientRect();
      const relativeX = clientX - rect.left;
      return Math.max(0, Math.min(1, relativeX / rect.width));
    },
    []
  );

  // Pointer drag gestures
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only primary touch or left mouse button
    if (e.button !== 0 && e.pointerType === 'mouse') return;

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    const ratio = calculateRatio(e.clientX);
    setIsDragging(true);
    setDragRatio(ratio);

    tg.haptic('light');
    lastHapticSecondRef.current = Math.floor(ratio * duration);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const ratio = calculateRatio(e.clientX);
    setDragRatio(ratio);

    // Subtle tactile haptic tick every 2 seconds during scrub
    const currentSec = Math.floor(ratio * duration);
    if (Math.abs(currentSec - lastHapticSecondRef.current) >= 2) {
      lastHapticSecondRef.current = currentSec;
      tg.haptic('light');
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}

    const finalRatio = calculateRatio(e.clientX);
    setIsDragging(false);
    setDragRatio(null);

    const seekTarget = finalRatio * duration;
    onSeek(seekTarget);
    tg.haptic('medium');
  };

  // Badge X position clamped so it doesn't overflow outside container
  const rawBadgeX = currentRatio * containerWidth;
  const badgeWidthEstimate = 120;
  const minBadgeX = badgeWidthEstimate / 2 + 6;
  const maxBadgeX = containerWidth - badgeWidthEstimate / 2 - 6;
  const clampedBadgeX = Math.max(minBadgeX, Math.min(maxBadgeX, rawBadgeX));

  return (
    <div
      ref={containerRef}
      className={`soundcloud-waveform-container ${isDragging ? 'dragging' : ''} ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        height: `${height}px`,
        touchAction: 'none',
        userSelect: 'none',
        cursor: 'pointer',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      role="slider"
      aria-label="SoundCloud Waveform Scrub Bar"
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={displayCurrentTime}
    >
      {/* 1. Waveform Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: `${height}px`,
          pointerEvents: 'none',
        }}
      />

      {/* 2. Floating Comments / Reactions Avatars (SoundCloud Style) */}
      {comments && comments.length > 0 && (
        <div className="sc-comments-layer" style={{ pointerEvents: 'none' }}>
          {comments.map((c) => {
            const commentX = c.timeRatio * containerWidth;
            return (
              <div
                key={c.id}
                className="sc-comment-pin"
                style={{
                  position: 'absolute',
                  left: `${commentX}px`,
                  bottom: `${height * 0.35 + 8}px`,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  background: 'rgba(20, 16, 13, 0.88)',
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  borderRadius: '12px',
                  padding: '2px 5px 2px 2px',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.6)',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                {c.avatarUrl ? (
                  <img
                    src={c.avatarUrl}
                    alt=""
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: 'var(--amber)',
                      fontSize: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 700,
                    }}
                  >
                    {c.author ? c.author.charAt(0).toUpperCase() : '♫'}
                  </div>
                )}
                {c.emoji && <span style={{ fontSize: '11px', lineHeight: 1 }}>{c.emoji}</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* 3. SoundCloud Center Time Badge [ 48:59 | 1:47:29 ] */}
      {showBadge && (
        <div
          className={`sc-time-badge ${isDragging ? 'active-drag' : ''}`}
          style={{
            position: 'absolute',
            left: `${clampedBadgeX}px`,
            top: `${height * 0.68}px`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            background: 'rgba(7, 6, 5, 0.94)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.22)',
            borderRadius: '4px',
            padding: '3px 8px',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.85)',
            zIndex: 10,
            whiteSpace: 'nowrap',
            transition: isDragging ? 'none' : 'left 80ms ease-out',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              fontWeight: 700,
              color: '#ffffff',
              letterSpacing: '0.4px',
            }}
          >
            {formatTime(displayCurrentTime)}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '10px',
              color: 'rgba(255, 255, 255, 0.42)',
              fontWeight: 400,
            }}
          >
            |
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.82)',
              letterSpacing: '0.4px',
            }}
          >
            {formatTime(duration)}
          </span>
        </div>
      )}
    </div>
  );
};
